import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useI18n } from "../../../hooks/useTranslations";
import { useCostTypesApi } from "../../../http/costTypes/api";
import { UpsertCostType } from "../../../http/costTypes/types";
import ModalDialog from "../../ui/ModalDialog";
import CostTypeForm, { CostTypeFormBody } from "./CostTypeForm";

type EditCostTypeDialogProps = {
  costTypeId: string;
  onClose: () => void;
};

const EditCostTypeDialog: FC<EditCostTypeDialogProps> = ({ costTypeId, onClose }: EditCostTypeDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const costTypesApi = useCostTypesApi();

  const costTypeSwr = costTypesApi.getCostType(costTypeId, aborter.current.signal);
  const [costType, isInitialized] = useDataInitializer(costTypeSwr.data, costTypeSwr.error);

  useEffect(() => {
    costTypeId && costTypeSwr.trigger();
  }, [costTypeId]);

  useEffect(() => {
    costTypeSwr.error && dispatch(showErrorNotification(i18n, costTypeSwr.error));
  }, [costTypeSwr.error]);

  const onSubmit = async (formData: CostTypeFormBody): Promise<void> => {
    if (costType?.id) {
      const body: UpsertCostType = {
        categoryId: formData.category.id,
        name: formData.name,
        dkvProductCodes: formData.dkvProductCodes,
        pkwAccountingNumber: formData.pkwAccountingNumber,
        lkwAccountingNumber: formData.lkwAccountingNumber,
        pkwSuperiorAccountingNumber: formData.pkwSuperiorAccountingNumber,
        costObject: formData.costObject,
        currentVersionNumber: costType.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await costTypesApi.updateAdminCostType(costType.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose();
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.costTypes.edit}>
      <CostTypeForm
        onSubmit={onSubmit}
        isDisabled={!userSettings.permissions.costTypes.update || isSubmitting || !costType?.id}
        isLoading={!isInitialized}
        costType={costType}
      />
    </ModalDialog>
  );
};

export default EditCostTypeDialog;
